import {lazy, Suspense} from 'react';
import {Routes, Route, Navigate} from 'react-router';

import Layout from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import Impressum from './components/Impressum';
import Datenschutz from './components/Datenschutz';
// ⚠️ Eager, unlike every page below. A 404 is reached by a *failed* navigation, often from an
// external link, and a lazy chunk would put a network round-trip between the dead URL and the
// explanation of why it is dead — the one page that must not itself hang. It is small and has
// no fixture.
import NotFound from './components/NotFound';
// Phase 5. `/vorgang/:id` is the rebuild's detail page and reads the rebuild's API.
// ☠️ `/law/:id` was the previous implementation's twin and stood here until the cutover;
// the ids are identical, so a redirect was possible and was declined (`docs/CUTOVER.md`).
//
// Lazy, because a bill page is the *second* page a visitor sees, never the first. Bundled
// eagerly it added 5kB gzipped to an initial bundle already 33kB over the landing-page
// budget; split, it costs the initial load nothing and arrives while the fetch is in flight.
const VorgangPage = lazy(() => import('./components/vorgang/VorgangPage'));
// Lazily, so neither the gallery nor the 22kB real-data fixture it renders lands in the
// bundle a visitor downloads. It is a development surface, not a page of the site.
const DesignGallery = lazy(() => import('./components/design/DesignGallery'));
// Same reasoning, and it matters more here: `fixtures/details.json` is 484kB of real corpus
// data. Lazy means neither the gallery nor its fixture reaches a visitor's bundle.
const DetailGallery = lazy(() => import('./components/design/DetailGallery'));
// The methodology page. Lazy because it is a footer page nobody loads on arrival, and it is
// where every bill page's "how this was made" explanation now lives instead of on the page.
const MethodikPage = lazy(() => import('./components/methodik/MethodikPage'));
// The bill list. Supersedes the Phase 7 pipeline page: that was this list with one filter
// applied and grouped by phase, and two pages over one corpus would be two definitions of
// "the list of bills". `/im-verfahren` redirects here with those parameters set.
const BillsPage = lazy(() => import('./components/bills/BillsPage'));
// Its fixture-backed twin, so `check:frontend` can cover this page's layout in CI.
const BillsGallery = lazy(() => import('./components/design/BillsGallery'));
// W1 · the rebuild's landing page, and since 2026-09-05 the site's root. It waited for
// `/themen` and `/gesetze`, because flipping earlier would have deleted the previous
// homepage's topic and party entry points and replaced them with nothing.
// The sitting-week review. It was the landing page until W1 was rebuilt around one debate;
// what it holds — every bill decided, first-read and debated that week, plus the Aktuelle
// Stunden — is the orientation content the landing page deliberately does not carry. It keeps
// its own route and is linked from the landing page's closing strip.
const RueckblickPage = lazy(() => import('./components/sitzungswoche/SitzungswochePage'));
// Its fixture-backed twin, so `check:frontend` can cover this page's layout in CI.
const SitzungswocheGallery = lazy(() => import('./components/design/SitzungswocheGallery'));
// W1 rebuilt: one lead debate in depth rather than a list of the week's cards. It keeps the
// `/sitzungswoche` path — the page is still anchored to the last sitting week, and the week
// itself survives as the closing strip — so nothing that links here has to change.
// W1 as it now stands: seven panels on the last sitting week, everything on one screen at
// desktop width. It replaces the one-debate page at `/sitzungswoche`, which survives at
// `/leitdebatte` — its matrix and background are the depth this overview links into.
const DashboardPage = lazy(() => import('./components/dashboard/DashboardPage'));
const DashboardGallery = lazy(() => import('./components/design/DashboardGallery'));
const LandingPage = lazy(() => import('./components/landing/LandingPage'));
const LandingGallery = lazy(() => import('./components/design/LandingGallery'));
// Phase 9 · W4. `/parteien` is a list of names and `/partei/:slug` is one Fraktion's record.
// Lazy for the same reason as every other rebuild page — neither is a landing surface, and
// the initial bundle is already over the 150kB budget for one.
const PartyIndexPage = lazy(() => import('./components/party/PartyIndexPage'));
const PartyPage = lazy(() => import('./components/party/PartyPage'));
// Its fixture-backed twin. 88kB of real corpus data for all five Fraktionen, so lazy keeps it
// off a visitor's bundle entirely.
const PartyGallery = lazy(() => import('./components/design/PartyGallery'));
// The theme dossiers. `/themen` is the ten policy areas; `/thema/:slug` is one area's record.
const ThemeIndexPage = lazy(() => import('./components/themes/ThemeIndexPage'));
const ThemePage = lazy(() => import('./components/themes/ThemePage'));
const ThemeGallery = lazy(() => import('./components/design/ThemeGallery'));
// The Bundesland dossiers. Brought back with `/`: the previous homepage had tiles for topics,
// Fraktionen and Bundesländer, and deleting it left the third with no entry point at all.
const LandIndexPage = lazy(() => import('./components/laender/LandIndexPage'));
const LandPage = lazy(() => import('./components/laender/LandPage'));
const LandGallery = lazy(() => import('./components/design/LandGallery'));
// Phase 12. Component states rather than page variants, so one route holds all eight.
const ChatGallery = lazy(() => import('./components/design/ChatGallery'));

export default function Router() {
    return (
        // ☠️ A render error must not blank the site. One missing API field once took the whole
        // page to white — see `ErrorBoundary`. It wraps the routes rather than sitting inside
        // Layout so a crash in the shell itself is caught too.
        <ErrorBoundary>
        <Routes>
            <Route element={<Layout/>}>
                {/* ☠️ The flip. `/` was the previous implementation's homepage — emotional
                    framing, a search box, and tiles for topics, Fraktionen and Bundesländer.
                    All three came back as their own dossiers: `/themen`, `/parteien` and
                    `/laender`. The old `/laws/:bundesland` route went at the cutover. */}
                <Route index element={
                    <Suspense fallback={null}><DashboardPage/></Suspense>
                }></Route>
                {/* The landing page's own URL for the four phases before it became the root.
                    Redirected rather than duplicated, so there is one landing page. */}
                <Route path="sitzungswoche" element={<Navigate replace to="/"/>}></Route>
                <Route path="leitdebatte" element={
                    <Suspense fallback={null}><LandingPage/></Suspense>
                }></Route>
                <Route path="rueckblick" element={
                    <Suspense fallback={null}><RueckblickPage/></Suspense>
                }></Route>
                {/* ⚠️ A second, explicit route rather than `rueckblick/:woche?`. The optional
                    form works, but two plain routes are unambiguous and the archive URL is
                    the page's identity — it must be shareable and it must not depend on a
                    router feature behaving as expected. */}
                <Route path="rueckblick/:woche" element={
                    <Suspense fallback={null}><RueckblickPage/></Suspense>
                }></Route>
                <Route path="gesetze" element={
                    <Suspense fallback={null}><BillsPage/></Suspense>
                }></Route>
                {/* The pipeline's URL, kept working. It is this list with a filter and a sort,
                    so it redirects rather than being reimplemented — and every existing link,
                    in the footer and on the front page's Bilanz panel, still lands somewhere
                    meaningful. `replace` so Back does not bounce off it. */}
                <Route path="im-verfahren" element={
                    <Navigate replace to="/gesetze?status=im-verfahren&sort=stand"/>
                }></Route>
                <Route path="vorgang/:id" element={
                    <Suspense fallback={null}><VorgangPage/></Suspense>
                }></Route>
                <Route path="themen" element={
                    <Suspense fallback={null}><ThemeIndexPage/></Suspense>
                }></Route>
                <Route path="thema/:slug" element={
                    <Suspense fallback={null}><ThemePage/></Suspense>
                }></Route>
                <Route path="laender" element={
                    <Suspense fallback={null}><LandIndexPage/></Suspense>
                }></Route>
                <Route path="land/:slug" element={
                    <Suspense fallback={null}><LandPage/></Suspense>
                }></Route>
                <Route path="parteien" element={
                    <Suspense fallback={null}><PartyIndexPage/></Suspense>
                }></Route>
                <Route path="partei/:slug" element={
                    <Suspense fallback={null}><PartyPage/></Suspense>
                }></Route>
                <Route path="impressum" element={<Impressum/>}></Route>
                <Route path="datenschutz" element={<Datenschutz/>}></Route>
                <Route path="methodik" element={
                    <Suspense fallback={null}><MethodikPage/></Suspense>
                }></Route>
                {/* ☠️ Inside Layout, so a dead end keeps the header, the nav and the footer —
                    a visitor who mistyped or followed a retired link can leave by clicking
                    rather than by editing the URL. */}
                <Route path="*" element={<NotFound/>}></Route>
            </Route>
            {/* Phase 4 development surface: outside Layout on purpose, so the legacy
                header and its stylesheet cannot flatter or distort the design system. */}
            <Route path="design" element={
                <Suspense fallback={null}><DesignGallery/></Suspense>
            }></Route>
            {/* Phase 5. One case per route — a detail page is met on its own, and stacking
                eight of them would test a scroll container rather than a page. */}
            <Route path="design/gesetze" element={
                <Suspense fallback={null}><BillsGallery/></Suspense>
            }></Route>
            <Route path="design/gesetze/gruppiert" element={
                <Suspense fallback={null}><BillsGallery/></Suspense>
            }></Route>
            <Route path="design/dashboard" element={
                <Suspense fallback={null}><DashboardGallery/></Suspense>
            }></Route>
            <Route path="design/landing" element={
                <Suspense fallback={null}><LandingGallery/></Suspense>
            }></Route>
            <Route path="design/sitzungswoche" element={
                <Suspense fallback={null}><SitzungswocheGallery/></Suspense>
            }></Route>
            <Route path="design/vorgang" element={
                <Suspense fallback={null}><DetailGallery/></Suspense>
            }></Route>
            <Route path="design/vorgang/:slug" element={
                <Suspense fallback={null}><DetailGallery/></Suspense>
            }></Route>
            {/* Phase 9. All five Fraktionen are routes, because they are the layout's range
                rather than five samples of it — see `PartyGallery`. */}
            <Route path="design/chat" element={
                <Suspense fallback={null}><ChatGallery/></Suspense>
            }></Route>
            <Route path="design/land" element={
                <Suspense fallback={null}><LandGallery/></Suspense>
            }></Route>
            <Route path="design/land/:slug" element={
                <Suspense fallback={null}><LandGallery/></Suspense>
            }></Route>
            <Route path="design/thema" element={
                <Suspense fallback={null}><ThemeGallery/></Suspense>
            }></Route>
            <Route path="design/thema/:slug" element={
                <Suspense fallback={null}><ThemeGallery/></Suspense>
            }></Route>
            <Route path="design/party" element={
                <Suspense fallback={null}><PartyGallery/></Suspense>
            }></Route>
            <Route path="design/party/:slug" element={
                <Suspense fallback={null}><PartyGallery/></Suspense>
            }></Route>
        </Routes>
        </ErrorBoundary>
    )
}